import { emailSchema } from "@eb/shared";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { Button, ErrorBanner } from "../components/ui";
import { useAuth } from "../state/auth";
import { AuthShell, Field } from "./LoginPage";

/**
 * Step one of the password reset: ask Supabase to mail a recovery link.
 *
 * The confirmation screen deliberately does not claim a mail was sent. GoTrue answers
 * identically for an address it has never seen, so saying "sent" would turn this form into
 * an oracle for which emails are registered.
 */
export function ForgotPasswordPage() {
    const { requestPasswordReset } = useAuth();
    const [email, setEmail] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        setError(null);

        const parsed = emailSchema.safeParse(email);
        if (!parsed.success) {
            setError(parsed.error.issues[0]?.message ?? "请输入有效的邮箱地址");
            return;
        }

        setBusy(true);
        try {
            await requestPasswordReset(parsed.data);
            setSubmitted(true);
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "发送失败，请稍后重试");
        } finally {
            setBusy(false);
        }
    }

    if (submitted) {
        return (
            <AuthShell title="查收邮件" subtitle="重置链接已经在路上">
                <div className="space-y-4 text-sm text-ink-soft">
                    <p>
                        如果 <strong className="text-ink">{email}</strong> 是已注册的邮箱，
                        我们已经发出了一封重置密码的邮件，点开里面的链接就能设置新密码。
                    </p>
                    <p>链接一小时内有效。没看到的话，也翻一下垃圾邮件。</p>
                    <Link to="/login" className="block text-center font-medium text-brand hover:underline">
                        回到登录
                    </Link>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell title="忘记密码" subtitle="填邮箱，我们发一个重置链接过去">
            <form onSubmit={handleSubmit} className="space-y-4">
                {error ? <ErrorBanner message={error} /> : null}

                <Field
                    label="邮箱"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                />

                <Button type="submit" loading={busy} className="w-full">
                    发送重置链接
                </Button>

                <p className="text-center text-sm text-ink-soft">
                    想起来了？{" "}
                    <Link to="/login" className="font-medium text-brand hover:underline">
                        去登录
                    </Link>
                </p>
            </form>
        </AuthShell>
    );
}
